import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, Copy, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  DeviceContactMultiButton,
  type PickedContact,
} from '@/components/DeviceContactMultiButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { createBulkInvitation, createRegistrationLink } from '@/services/api.client';
import type {
  CreateBulkInvitationBody,
  CreateBulkInvitationResult,
  CreateRegistrationLinkBody,
  RegistrationLinkResult,
} from '@/types';

/**
 * Shared event-invite forms used across resident, enterprise tenant, and
 * employee surfaces. The server-side bulk-invitation + registration-link
 * endpoints permit any role at or above resident in the hierarchy
 * (resident=40, employee=50, enterprise_tenant_admin=70), so all three
 * surfaces post to the same `/properties/:propertyId/invitations/bulk`
 * and `/properties/:propertyId/registration-links` endpoints.
 *
 * Per-surface differences live in the page shells (copy, default tab,
 * propertyId source). The forms themselves are surface-agnostic — they
 * only need a propertyId and optional copy overrides.
 */

// Pass is valid the entire day of the event (midnight to 23:59).
function eventDayWindow(eventDateString: string): { validFrom: string; validUntil: string } {
  const parts = eventDateString.split('-').map((s) => Number(s));
  const y = parts[0] ?? new Date().getFullYear();
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const start = new Date(y, m - 1, d, 0, 0, 0, 0);
  const end = new Date(y, m - 1, d, 23, 59, 59, 999);
  const now = new Date();
  const validFrom = start < now && end > now ? now : start;
  return { validFrom: validFrom.toISOString(), validUntil: end.toISOString() };
}

// ── Bulk invite form ─────────────────────────────────────────────────────────

const visitorSchema = z.object({
  visitorName: z.string().min(2, 'Enter the visitor name'),
  visitorPhone: z.string().min(6, 'Enter a valid phone number'),
  visitorEmail: z.union([z.string().email('Invalid email'), z.literal('')]).optional(),
  vehiclePlate: z.string().optional(),
  idNumber: z.string().optional(),
  partySize: z.union([z.string(), z.literal('')]).optional(),
});

const bulkSchema = z.object({
  eventName: z.string().min(2, 'Enter the event name'),
  eventDate: z.string().min(1, 'Choose a date'),
  visitors: z.array(visitorSchema).min(1, 'Add at least one visitor'),
});

type BulkFormValues = z.infer<typeof bulkSchema>;

export interface BulkInviteFormProps {
  propertyId: string;
  /** Default placeholder for the event name (e.g. "Quarterly town hall" for enterprise). */
  eventNamePlaceholder?: string;
  /** Override label for the visitor list (e.g. "Your guest list" -> "Attendee list"). */
  visitorListLabel?: string;
  /** Singular noun for individual rows (e.g. "Guest" -> "Attendee"). */
  visitorRowNoun?: string;
}

export function BulkInviteForm({
  propertyId,
  eventNamePlaceholder = 'Birthday Party',
  visitorListLabel = 'Your guest list',
  visitorRowNoun = 'Guest',
}: BulkInviteFormProps) {
  const [result, setResult] = useState<CreateBulkInvitationResult | null>(null);

  const form = useForm<BulkFormValues>({
    resolver: zodResolver(bulkSchema),
    defaultValues: {
      eventName: '',
      eventDate: '',
      visitors: [
        {
          visitorName: '',
          visitorPhone: '',
          visitorEmail: '',
          vehiclePlate: '',
          idNumber: '',
          partySize: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'visitors' });

  // Fill from the device phone book (multi-select). Reuses any blank rows first,
  // then appends the rest, capped at the server's bulk limit. Fill-only — nothing
  // about the device contact list is stored or transmitted.
  const handleContactsPicked = (picked: PickedContact[]) => {
    const MAX_VISITORS = 100;
    const current = form.getValues('visitors');
    let cursor = 0;

    // Reuse rows that are entirely empty (no name and no phone).
    for (let i = 0; i < current.length && cursor < picked.length; i++) {
      const row = current[i];
      const isBlank = !row?.visitorName?.trim() && !row?.visitorPhone?.trim();
      if (isBlank) {
        const c = picked[cursor];
        if (c) {
          form.setValue(`visitors.${i}.visitorName`, c.name, { shouldValidate: false });
          form.setValue(`visitors.${i}.visitorPhone`, c.phone, { shouldValidate: false });
        }
        cursor++;
      }
    }

    // Append the remainder up to the cap.
    for (; cursor < picked.length; cursor++) {
      if (form.getValues('visitors').length >= MAX_VISITORS) break;
      const c = picked[cursor];
      if (!c) continue;
      append({
        visitorName: c.name,
        visitorPhone: c.phone,
        visitorEmail: '',
        vehiclePlate: '',
        idNumber: '',
        partySize: '',
      });
    }
  };

  const mutation = useMutation({
    mutationFn: (body: CreateBulkInvitationBody) => createBulkInvitation(propertyId, body),
    onSuccess: (res) => {
      setResult(res);
      form.reset();
    },
  });

  const onSubmit = (values: BulkFormValues) => {
    const { validFrom, validUntil } = eventDayWindow(values.eventDate);
    const body: CreateBulkInvitationBody = {
      eventName: values.eventName,
      eventDate: new Date(values.eventDate).toISOString(),
      validFrom,
      validUntil,
      visitors: values.visitors.map((v) => ({
        visitorName: v.visitorName,
        visitorPhone: v.visitorPhone,
        ...(v.visitorEmail ? { visitorEmail: v.visitorEmail } : {}),
        ...(v.vehiclePlate ? { vehiclePlate: v.vehiclePlate } : {}),
        ...(v.idNumber ? { idNumber: v.idNumber } : {}),
        ...(v.partySize && Number(v.partySize) > 1 ? { partySize: Number(v.partySize) } : {}),
      })),
    };
    setResult(null);
    mutation.mutate(body);
  };

  const apiError =
    mutation.isError && mutation.error instanceof Object && 'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} noValidate className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="eventName">Event name</Label>
          <Input
            id="eventName"
            placeholder={eventNamePlaceholder}
            aria-invalid={!!form.formState.errors.eventName}
            {...form.register('eventName')}
          />
          {form.formState.errors.eventName && (
            <p className="text-sm text-destructive">{form.formState.errors.eventName.message}</p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="bulk-eventDate">Event date</Label>
          <Input
            id="bulk-eventDate"
            type="date"
            aria-invalid={!!form.formState.errors.eventDate}
            {...form.register('eventDate')}
          />
          {form.formState.errors.eventDate && (
            <p className="text-sm text-destructive">{form.formState.errors.eventDate.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Their pass is valid the entire day of the event.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>{visitorListLabel}</Label>
          <div className="flex items-center gap-2">
            <DeviceContactMultiButton onPick={handleContactsPicked} />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                append({
                  visitorName: '',
                  visitorPhone: '',
                  visitorEmail: '',
                  vehiclePlate: '',
                  idNumber: '',
                  partySize: '',
                })
              }
            >
              <Plus className="mr-1 size-4" />
              Add another
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {fields.map((field, i) => {
            const rowErrors = form.formState.errors.visitors?.[i];
            return (
              <div
                key={field.id}
                className="rounded-lg border border-border bg-muted/20 p-3 space-y-2 relative"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-muted-foreground">
                    {visitorRowNoun} {i + 1}
                  </p>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Remove visitor ${i + 1}`}
                      onClick={() => remove(i)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Input
                      placeholder="Full name"
                      aria-label={`Visitor ${i + 1} name`}
                      aria-invalid={!!rowErrors?.visitorName}
                      {...form.register(`visitors.${i}.visitorName`)}
                    />
                    {rowErrors?.visitorName?.message && (
                      <p className="text-xs text-destructive">{rowErrors.visitorName.message}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Input
                      placeholder="Phone"
                      type="tel"
                      aria-label={`Visitor ${i + 1} phone`}
                      aria-invalid={!!rowErrors?.visitorPhone}
                      {...form.register(`visitors.${i}.visitorPhone`)}
                    />
                    {rowErrors?.visitorPhone?.message && (
                      <p className="text-xs text-destructive">{rowErrors.visitorPhone.message}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="ID / Passport number (optional)"
                    aria-label={`Visitor ${i + 1} ID number`}
                    {...form.register(`visitors.${i}.idNumber`)}
                  />
                  <Input
                    placeholder="Vehicle plate (optional)"
                    aria-label={`Visitor ${i + 1} vehicle plate`}
                    {...form.register(`visitors.${i}.vehiclePlate`)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="Email (optional)"
                    type="email"
                    aria-label={`Visitor ${i + 1} email`}
                    {...form.register(`visitors.${i}.visitorEmail`)}
                  />
                  <Input
                    placeholder="Number of people (default 1)"
                    type="number"
                    min={1}
                    aria-label={`Visitor ${i + 1} party size`}
                    {...form.register(`visitors.${i}.partySize`)}
                  />
                </div>
                {rowErrors?.visitorEmail?.message && (
                  <p className="text-xs text-destructive">{rowErrors.visitorEmail.message}</p>
                )}
              </div>
            );
          })}
        </div>
        {form.formState.errors.visitors?.message && (
          <p className="text-sm text-destructive">{form.formState.errors.visitors.message}</p>
        )}
      </div>

      {apiError && (
        <p className="text-sm text-destructive" role="alert">
          {apiError}
        </p>
      )}
      {result && (
        <div
          className="rounded-md border border-border bg-muted/40 p-3 text-sm"
          data-testid="bulk-result"
        >
          Created {result.createdCount} invitation{result.createdCount === 1 ? '' : 's'}.
          {result.failed && result.failed.length > 0 && (
            <p className="mt-1 text-destructive">
              {result.failed.length} failed. Check the numbers and try again.
            </p>
          )}
        </div>
      )}

      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Sending…' : 'Send invitations'}
      </Button>
    </form>
  );
}

// ── Registration link form ──────────────────────────────────────────────────

const linkSchema = z.object({
  title: z.string().min(2, 'Enter an event title'),
  description: z.string().optional(),
  eventDate: z.string().min(1, 'Choose a date'),
  eventLocation: z.string().optional(),
  approvalMode: z.enum(['open', 'approval_required']),
  capacity: z.string().optional(),
});

type LinkFormValues = z.infer<typeof linkSchema>;

export interface RegistrationLinkFormProps {
  propertyId: string;
  titlePlaceholder?: string;
}

export function RegistrationLinkForm({
  propertyId,
  titlePlaceholder = 'Community AGM',
}: RegistrationLinkFormProps) {
  const [result, setResult] = useState<RegistrationLinkResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const form = useForm<LinkFormValues>({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      title: '',
      description: '',
      eventDate: '',
      eventLocation: '',
      approvalMode: 'open',
    },
  });

  const mutation = useMutation({
    mutationFn: (body: CreateRegistrationLinkBody) => createRegistrationLink(propertyId, body),
    onSuccess: (res) => setResult(res),
  });

  const onSubmit = (values: LinkFormValues) => {
    const capacityNum = values.capacity ? Number(values.capacity) : NaN;
    const body: CreateRegistrationLinkBody = {
      title: values.title,
      ...(values.description ? { description: values.description } : {}),
      eventDate: new Date(values.eventDate).toISOString(),
      ...(values.eventLocation ? { eventLocation: values.eventLocation } : {}),
      approvalMode: values.approvalMode,
      ...(Number.isFinite(capacityNum) && capacityNum > 0 ? { capacity: capacityNum } : {}),
    };
    setResult(null);
    mutation.mutate(body);
  };

  const apiError =
    mutation.isError && mutation.error instanceof Object && 'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  const copyUrl = async () => {
    if (!result) return;
    const full = `${window.location.origin}${result.url}`;
    try {
      await navigator.clipboard.writeText(full);
    } catch {
      // Clipboard not available in some environments — fall back silently.
    }
  };

  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} noValidate className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="link-title">Event title</Label>
        <Input
          id="link-title"
          placeholder={titlePlaceholder}
          aria-invalid={!!form.formState.errors.title}
          {...form.register('title')}
        />
        {form.formState.errors.title && (
          <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="link-description">Description</Label>
        <Textarea
          id="link-description"
          placeholder="Optional — shown on the public signup page"
          rows={3}
          {...form.register('description')}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="link-eventDate">Event date</Label>
          <Input
            id="link-eventDate"
            type="date"
            aria-invalid={!!form.formState.errors.eventDate}
            {...form.register('eventDate')}
          />
          {form.formState.errors.eventDate && (
            <p className="text-sm text-destructive">{form.formState.errors.eventDate.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="link-eventLocation">Location</Label>
          <Input
            id="link-eventLocation"
            placeholder="Optional"
            {...form.register('eventLocation')}
          />
        </div>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1.5 text-sm text-(--text-accent) hover:underline"
        >
          <ChevronDown
            className={`size-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
          />
          {showAdvanced ? 'Hide options' : 'More options'}
        </button>

        {showAdvanced && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="link-approvalMode">Who gets in?</Label>
              <Controller
                name="approvalMode"
                control={form.control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="link-approvalMode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Anyone with the link</SelectItem>
                      <SelectItem value="approval_required">Only people I approve</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="link-capacity">
                Maximum people <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="link-capacity"
                type="number"
                min={1}
                placeholder="No limit"
                {...form.register('capacity')}
              />
              <p className="text-xs text-muted-foreground">
                Sign-ups close automatically when your event starts. The pass is valid the whole
                day.
              </p>
            </div>
          </div>
        )}
      </div>

      {apiError && (
        <p className="text-sm text-destructive" role="alert">
          {apiError}
        </p>
      )}
      {result && (
        <div
          className="space-y-2 rounded-md border border-border bg-muted/40 p-3"
          data-testid="link-result"
        >
          <p className="text-sm">Link created. Share this URL with your guests:</p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={`${window.location.origin}${result.url}`}
              className="font-mono text-xs"
            />
            <Button type="button" size="sm" variant="outline" onClick={() => void copyUrl()}>
              <Copy className="mr-1 size-4" />
              Copy
            </Button>
          </div>
        </div>
      )}

      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Generating…' : 'Get my signup link'}
      </Button>
    </form>
  );
}
